import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Reusable N-character OTP / verification code input.
 *
 * Renders N small boxes that auto-advance on type, support Backspace,
 * arrow keys and paste of the full code at once. Accepts both digits
 * and letters (case-sensitive), filters only whitespace.
 */
function OtpInput({
  length = 4,
  value,
  onChange,
  autoFocus = true,
  disabled = false,
}) {
  const { t } = useTranslation();
  const inputsRef = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  const focusInput = (idx) => {
    const clamped = Math.max(0, Math.min(length - 1, idx));
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select?.();
  };

  const replaceAt = (idx, ch) => {
    const next = digits.slice();
    next[idx] = ch;
    onChange(next.join(""));
  };

  const handleChange = (idx, raw) => {
    const ch = raw.replace(/\s/g, "").slice(-1);
    replaceAt(idx, ch);
    if (ch && idx < length - 1) focusInput(idx + 1);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        replaceAt(idx, "");
      } else if (idx > 0) {
        focusInput(idx - 1);
        replaceAt(idx - 1, "");
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft") {
      focusInput(idx - 1);
      e.preventDefault();
    } else if (e.key === "ArrowRight") {
      focusInput(idx + 1);
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\s/g, "");
    if (!text) return;
    e.preventDefault();
    const chars = text.slice(0, length).split("");
    const next = digits.slice();
    for (let i = 0; i < length; i += 1) next[i] = chars[i] || "";
    onChange(next.join(""));
    focusInput(Math.min(chars.length, length - 1));
  };

  return (
    <div className="d-flex gap-2 gap-md-3 justify-content-center justify-content-md-start">
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          type="text"
          inputMode="text"
          autoComplete="one-time-code"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          maxLength={1}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          aria-label={t("authModal.verify.digitAria", { index: idx + 1 })}
          className="common-input text-center fw-600"
          style={{ width: 56, height: 64, fontSize: 28, padding: 0 }}
        />
      ))}
    </div>
  );
}

export default OtpInput;
